import { z } from 'zod'

import type { PlanSlug } from './planSlug'
import { LIGHT_PLAN_SLUG } from './planSlug'

export const tenantStatusSchema = z.enum(['preActive', 'trial', 'active', 'suspended', 'free'])
export type TenantStatus = z.infer<typeof tenantStatusSchema>
export const organizationTenantStatusSchema = z.enum(['active', 'suspended'])
export type OrganizationTenantStatus = z.infer<typeof organizationTenantStatusSchema>

// お試しプラン（status: free）で有料機能を無効化した箇所の、ツールチップ等に表示する共通文言。
export const FREE_PLAN_DISABLED_MESSAGE = 'この機能はお試しプランでは使えません'
// ライトプラン（plan.slug: light_tonnage_v1）でメタルタグ機能を無効化した箇所の共通文言。
export const LIGHT_PLAN_DISABLED_MESSAGE = 'この機能はライトプランでは使えません'

// メタルタグ機能（スキャン・手動入力・自動紐付け・未紐付け/重複一覧・スキャン画像の帳票出力・スマホアプリ）の
// 利用可否を判定するのに必要なテナント情報。status は TenantStatus、planSlug はテナントに紐づく
// 現在のプランの slug（プラン未設定なら null / undefined）。
// GraphQL の生成型（planSlug が optional）をそのまま渡せるよう planSlug は省略可にする。
export type MetalTagAvailabilityInput = {
  status: TenantStatus
  planSlug?: PlanSlug | null
}

// メタルタグ機能が使えないプランの表示名（ユーザー向け）を返す。使える場合は undefined。
// - お試しプラン（status: free）: 無償のため有料機能であるメタルタグ機能を提供しない
// - ライトプラン（slug: light_tonnage_v1）: 有料だがメタルタグ紐付けを含まない料金体系
// 料金ティアは status（free）と plan.slug（light）の2軸に分かれているため、各所で status だけを見ると
// ライトプランを取りこぼす。メタルタグ機能の出し分け・API 拒否は必ずこの関数（か下の派生関数）を通す。
// preActive / suspended はここでは見ない（それらはテナント全体のアクセス制御で別途扱う）。
export function metalTagUnavailablePlanName(tenant: MetalTagAvailabilityInput): string | undefined {
  if (tenant.status === 'free') {
    return 'お試しプラン'
  }
  if (tenant.planSlug === LIGHT_PLAN_SLUG) {
    return 'ライトプラン'
  }
  return undefined
}

// メタルタグ機能が使えない理由の文言（ツールチップ・API エラー用）。使える場合は undefined。
export function metalTagUnavailableMessage(tenant: MetalTagAvailabilityInput): string | undefined {
  if (tenant.status === 'free') {
    return FREE_PLAN_DISABLED_MESSAGE
  }
  if (tenant.planSlug === LIGHT_PLAN_SLUG) {
    return LIGHT_PLAN_DISABLED_MESSAGE
  }
  return undefined
}

export function isMetalTagAvailable(tenant: MetalTagAvailabilityInput): boolean {
  return metalTagUnavailableMessage(tenant) === undefined
}

export const tenantStatusLabels = {
  preActive: '未契約',
  trial: 'トライアル',
  active: '契約中',
  suspended: '停止',
  free: 'お試しプラン',
}

// 各ステータスの説明文。テナント作成・編集フォーム等でユーザー（ビジネス側含む）に
// 機能制限を分かりやすく伝えるために使う。将来テナント詳細画面等でも再利用できるよう shared に持たせる。
// free（お試しプラン）の制限内容は throwIfFreePlan / throwIfMetalTagUnavailable / UI disabled 箇所（FREE_PLAN_DISABLED_MESSAGE）に準拠する。
export const tenantStatusDescriptions: Record<TenantStatus, string> = {
  preActive: '未契約のテナント。契約前の商談・案内段階で利用します。',
  trial: 'トライアル中のテナント。（現在は原則利用しません）',
  active: '契約中のテナント。すべての機能を利用できます。',
  suspended: '利用を停止したテナント。ログイン・機能利用ができません。',
  free:
    'お試しプラン: 契約前のテナント向け。ミルシートの受取・閲覧・検索、ミルシートPDF・CSVのダウンロードのみ利用できます。\n' +
    'メタルタグ関連機能（スキャン・手動入力・スマホアプリ）、納品書PDFの登録、集計表・一覧表などの帳票出力は利用できません。',
}

export const tenantStatusOptions = Object.entries(tenantStatusLabels).map(([key, label]) => ({
  value: key,
  label,
}))

export const organizationTenantStatusOptions = tenantStatusOptions.filter(
  (x) => x.value === 'active' || x.value === 'suspended',
)

export const tenantStatusColors: Record<TenantStatus, string> = {
  preActive: 'yellow',
  trial: 'blue',
  active: 'green',
  suspended: 'gray',
  free: 'cyan',
}

// スキャンを利用可能かどうか判定（今後のステータス追加で漏れがでないように、Recordで管理している）
// TODO: 本来であればバックエンドのAPIでも細かくこれを判定すべきだが、現状はフロントエンドのみで判定しているので、APIを直で叩けば使えてしまう状態になっている
const isScanAvailableMapper: Record<TenantStatus, boolean> = {
  preActive: false,
  trial: true,
  active: true,
  suspended: false,
  // お試しプランはメタルタグ機能を提供しないため、スキャン（スマホアプリ）は利用不可
  free: false,
}
export function isScanAvailable(tenantStatus: TenantStatus) {
  return isScanAvailableMapper[tenantStatus]
}
